"use client";

import { useState } from "react";
import Image from "next/image";
import { api } from "@/lib/trpc/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Commit {
  sha: string;
  message: string;
  fullMessage: string;
  author: string;
  authorAvatar: string | null;
  date: string;
  url: string;
  repo: string;
}

interface PR {
  number: number;
  title: string;
  state: string;
  createdAt: string;
  mergedAt: string | null;
  url: string;
  author: string;
  authorAvatar: string | null;
  labels: Array<{ name: string; color: string }>;
  repo: string;
}

interface Contributor {
  login: string;
  avatar: string;
  contributions: number;
  url: string;
  repos: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const d = new Date(dateStr).getTime();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function getCommitImportance(message: string): {
  level: "critical" | "feature" | "fix" | "refactor" | "chore" | "docs";
  color: string;
  label: string;
} {
  const lower = message.toLowerCase();
  if (lower.includes("breaking") || lower.includes("security") || lower.includes("vulnerability") || lower.includes("critical") || lower.includes("emergency")) {
    return { level: "critical", color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Critical" };
  }
  if (lower.startsWith("feat") || lower.includes("add ") || lower.includes("implement") || lower.includes("new ") || lower.includes("introduce")) {
    return { level: "feature", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Feature" };
  }
  if (lower.startsWith("fix") || lower.includes("bug") || lower.includes("patch") || lower.includes("resolve") || lower.includes("hotfix")) {
    return { level: "fix", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Fix" };
  }
  if (lower.startsWith("refactor") || lower.includes("cleanup") || lower.includes("restructure") || lower.includes("optimize") || lower.includes("perf")) {
    return { level: "refactor", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Refactor" };
  }
  if (lower.startsWith("doc") || lower.includes("readme") || lower.includes("changelog")) {
    return { level: "docs", color: "bg-gray-500/20 text-gray-400 border-gray-500/30", label: "Docs" };
  }
  return { level: "chore", color: "bg-gray-500/20 text-gray-400 border-gray-500/30", label: "Chore" };
}

function getRepoShortName(repoFullName: string): string {
  return repoFullName.split("/").pop() ?? repoFullName;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CommitTimeline({ commits }: { commits: Commit[] }) {
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? commits : commits.slice(0, 15);

  // Group commits by day
  const grouped = new Map<string, Commit[]>();
  for (const c of displayed) {
    const day = new Date(c.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    if (!grouped.has(day)) grouped.set(day, []);
    grouped.get(day)!.push(c);
  }

  return (
    <div className="space-y-6">
      {[...grouped.entries()].map(([day, dayCommits]) => (
        <div key={day}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-blue-400" />
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">{day}</span>
            <span className="text-xs text-gray-600">({dayCommits.length} commits)</span>
          </div>
          <div className="ml-3 border-l border-[hsl(var(--border))] pl-4 space-y-2">
            {dayCommits.map((commit) => {
              const importance = getCommitImportance(commit.message);
              return (
                <a
                  key={commit.sha}
                  href={commit.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors"
                >
                  {commit.authorAvatar ? (
                    <Image
                      src={commit.authorAvatar}
                      alt={commit.author}
                      width={24}
                      height={24}
                      className="rounded-full mt-0.5 shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                      {commit.author.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${importance.color}`}>
                        {importance.label}
                      </span>
                      <span className="text-sm text-white group-hover:text-blue-400 transition-colors truncate">
                        {commit.message}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                      <span className="font-medium text-gray-400">{commit.author}</span>
                      <span>·</span>
                      <span className="px-1.5 py-0.5 rounded bg-white/5 text-gray-500 font-mono text-[10px]">
                        {getRepoShortName(commit.repo)}
                      </span>
                      <span>·</span>
                      <span>{timeAgo(commit.date)}</span>
                      <span className="font-mono text-gray-600">{commit.sha.slice(0, 7)}</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      ))}
      {!showAll && commits.length > 15 && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full py-2 text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
        >
          Show all {commits.length} commits
        </button>
      )}
    </div>
  );
}

function PRList({ prs }: { prs: PR[] }) {
  return (
    <div className="space-y-2">
      {prs.map((pr) => (
        <a
          key={`${pr.repo}-${pr.number}`}
          href={pr.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-start gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors"
        >
          {/* PR state icon */}
          <div className="mt-0.5 shrink-0">
            {pr.state === "merged" ? (
              <svg className="w-5 h-5 text-purple-400" viewBox="0 0 16 16" fill="currentColor">
                <path d="M5.45 5.154A4.25 4.25 0 0 0 9.25 7.5h1.378a2.251 2.251 0 1 1 0 1.5H9.25A5.734 5.734 0 0 1 5 7.123v3.505a2.25 2.25 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.95-.218zM4.25 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5zm8-9a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5zM4.25 4a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z" />
              </svg>
            ) : pr.state === "open" ? (
              <svg className="w-5 h-5 text-emerald-400" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1.5 3.25a2.25 2.25 0 1 1 3 2.122v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 1.5 3.25Zm5.677-.177L9.573.677A.25.25 0 0 1 10 .854V2.5h1A2.5 2.5 0 0 1 13.5 5v5.628a2.251 2.251 0 1 1-1.5 0V5a1 1 0 0 0-1-1h-1v1.646a.25.25 0 0 1-.427.177L7.177 3.427a.25.25 0 0 1 0-.354ZM3.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm0 9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm8.25.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-red-400" viewBox="0 0 16 16" fill="currentColor">
                <path d="M3.25 1A2.25 2.25 0 0 1 4 5.372v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 3.25 1Zm9.5 5.5a.75.75 0 0 1 .75.75v3.378a2.251 2.251 0 1 1-1.5 0V7.25a.75.75 0 0 1 .75-.75Zm-2.03-5.273a.75.75 0 0 1 1.06 0l2 2a.75.75 0 0 1-1.06 1.06L12 3.56v2.69a.75.75 0 0 1-1.5 0V3.56l-.72.72a.75.75 0 0 1-1.06-1.06l2-2ZM3.25 12.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm9.5 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-white group-hover:text-blue-400 transition-colors">
                {pr.title}
              </span>
              {pr.labels.map((label) => (
                <span
                  key={label.name}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border"
                  style={{
                    backgroundColor: `#${label.color}20`,
                    color: `#${label.color}`,
                    borderColor: `#${label.color}40`,
                  }}
                >
                  {label.name}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
              <span className="font-medium text-gray-400">{pr.author}</span>
              <span>·</span>
              <span className="px-1.5 py-0.5 rounded bg-white/5 text-gray-500 font-mono text-[10px]">
                {getRepoShortName(pr.repo)}
              </span>
              <span>·</span>
              <span>#{pr.number}</span>
              <span>·</span>
              <span>{timeAgo(pr.createdAt)}</span>
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

function ContributorGrid({ contributors }: { contributors: Contributor[] }) {
  const maxContribs = Math.max(...contributors.map((c) => c.contributions), 1);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {contributors.slice(0, 20).map((c, i) => (
        <a
          key={c.login}
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors"
        >
          <span className="text-xs text-gray-600 w-5 text-right shrink-0">{i + 1}</span>
          <Image
            src={c.avatar}
            alt={c.login}
            width={32}
            height={32}
            className="rounded-full shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors truncate">
                {c.login}
              </span>
              <span className="text-xs text-gray-500 font-mono">
                {c.contributions.toLocaleString()} commits
              </span>
            </div>
            <div className="mt-1.5 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 transition-all duration-500"
                style={{ width: `${(c.contributions / maxContribs) * 100}%` }}
              />
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

function RepoCards({ repos }: { repos: Array<{ repoFullName: string; stars: number; forks: number; openIssues: number; language: string | null }> }) {
  const LANG_COLORS: Record<string, string> = {
    Rust: "#dea584", Go: "#00ADD8", TypeScript: "#3178c6", JavaScript: "#f1e05a",
    Solidity: "#AA6746", Python: "#3572A5", "C++": "#f34b7d", C: "#555555",
    Haskell: "#5e5086", Move: "#4a137a", Java: "#b07219", Kotlin: "#A97BFF",
    Ruby: "#701516", Shell: "#89e051", Nix: "#7e7eff", OCaml: "#3be133",
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {repos.map((repo) => (
        <a
          key={repo.repoFullName}
          href={`https://github.com/${repo.repoFullName}`}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex flex-col p-4 rounded-xl bg-[hsl(230,15%,10%)] hover:bg-[hsl(230,15%,13%)] border border-[hsl(var(--border))] transition-colors"
        >
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-4 h-4 text-gray-500" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z" />
            </svg>
            <span className="font-medium text-blue-400 group-hover:text-blue-300 truncate text-sm transition-colors">
              {repo.repoFullName}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500 mt-auto">
            {repo.language && (
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: LANG_COLORS[repo.language] ?? "#6b7280" }} />
                {repo.language}
              </span>
            )}
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              {repo.stars.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              {repo.forks.toLocaleString()}
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              {repo.openIssues.toLocaleString()}
            </span>
          </div>
        </a>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

type Tab = "commits" | "prs" | "contributors" | "repos";

export function GitHubActivitySection({ slug }: { slug: string }) {
  const [activeTab, setActiveTab] = useState<Tab>("commits");

  const { data, isLoading, error } = api.token.getLiveGithubActivity.useQuery(
    { slug },
    { staleTime: 5 * 60 * 1000 }
  );

  if (isLoading) {
    return (
      <section className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">
        <div className="flex items-center gap-3 mb-4">
          <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-gray-400">Loading live GitHub activity...</span>
        </div>
      </section>
    );
  }

  if (error || !data || !data.org) {
    return null; // No GitHub data available
  }

  const { recentCommits, recentPRs, contributors, repos } = data;

  if (recentCommits.length === 0 && recentPRs.length === 0 && contributors.length === 0) {
    return null;
  }

  // Commit importance distribution
  const importanceCounts = { critical: 0, feature: 0, fix: 0, refactor: 0, docs: 0, chore: 0 };
  for (const c of recentCommits) {
    const imp = getCommitImportance(c.message);
    importanceCounts[imp.level]++;
  }
  const totalCategorized = Object.values(importanceCounts).reduce((a, b) => a + b, 0);

  const tabs: Array<{ key: Tab; label: string; count: number }> = [
    { key: "commits", label: "Recent Commits", count: recentCommits.length },
    { key: "prs", label: "Pull Requests", count: recentPRs.length },
    { key: "contributors", label: "Contributors", count: contributors.length },
    { key: "repos", label: "Repositories", count: repos.length },
  ];

  return (
    <section className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-[hsl(var(--border))]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-gray-400" viewBox="0 0 98 96" fill="currentColor">
              <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" />
            </svg>
            <div>
              <h2 className="text-lg font-semibold text-white">Live GitHub Activity</h2>
              <a
                href={`https://github.com/${data.org}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                github.com/{data.org}
              </a>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-xs text-gray-500">Live</span>
          </div>
        </div>

        {/* Commit importance breakdown */}
        {totalCategorized > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {importanceCounts.feature > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                {importanceCounts.feature} Features
              </span>
            )}
            {importanceCounts.fix > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                {importanceCounts.fix} Fixes
              </span>
            )}
            {importanceCounts.refactor > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
                {importanceCounts.refactor} Refactors
              </span>
            )}
            {importanceCounts.critical > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                {importanceCounts.critical} Critical
              </span>
            )}
            {(importanceCounts.chore + importanceCounts.docs) > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gray-500/10 text-gray-400 border border-gray-500/20">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                {importanceCounts.chore + importanceCounts.docs} Other
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center border-b border-[hsl(var(--border))] overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap border-b-2 ${
              activeTab === tab.key
                ? "text-blue-400 border-blue-400"
                : "text-gray-500 border-transparent hover:text-gray-300 hover:border-gray-600"
            }`}
          >
            {tab.label}
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
              activeTab === tab.key ? "bg-blue-500/20 text-blue-400" : "bg-white/5 text-gray-500"
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="p-6 max-h-[700px] overflow-y-auto">
        {activeTab === "commits" && <CommitTimeline commits={recentCommits} />}
        {activeTab === "prs" && <PRList prs={recentPRs} />}
        {activeTab === "contributors" && <ContributorGrid contributors={contributors} />}
        {activeTab === "repos" && <RepoCards repos={repos} />}
      </div>
    </section>
  );
}
